// frontend/src/pages/DashboardPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import Sidebar               from '../components/Sidebar';
import ProfilePage           from './ProfilePage';
import MainDashboardContent  from './dashboard/MainDashboardContent';
import AnalyseTurpe          from './admin/AnalyseTurpe';
import PDLDashboardPage      from './admin/PDLDashboardPage';
import PDLDocumentDetailPage from './admin/PDLDocumentDetailPage';
import PCEDashboardPage      from './admin/PCEDashboardPage';
import PCEDocumentDetailPage from './admin/PCEDocumentDetailPage';
import MyDocumentsPage       from './MyDocumentsPage';
import AdminDashboard        from './admin/AdminDashboard';
import ClientDetailPage      from './admin/ClientDetailPage';
import AnalyseCspe           from './admin/AnalyseCspe';
import ProspectCspe          from './admin/ProspectCspe';
import AnalyseTicgn          from './admin/AnalyseTicgn';
import BankPage from './admin/BankPage';
import LclPage from './admin/lcl/LclPage';
import CreUpdatesPage from './admin/cre/CreUpdatesPage';
import RevenueManagementPage from './admin/RevenueManagementPage';
import AuditEnedisGlobal from './admin/AuditEnedisGlobal';
import EnedisCertExpiryBanner from '../components/admin/EnedisCertExpiryBanner';

// ─── Helper ───────────────────────────────────────────────────────────────────
// rang 0 = super-admin | rang 8 = auditeur → accès dashboard admin identique
const isAdminLike = (profile) => {
  const rang = Number(profile?.rang ?? -1);
  return rang === 0 || rang === 8;
};

// ─── Composant ────────────────────────────────────────────────────────────────
const DashboardPage = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { session, userProfile, refreshUserProfile } = useOutletContext();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // ✅ Redirection automatique vers /dashboard/admin pour rang 0 et rang 8
  useEffect(() => {
    if (!userProfile) return;
    if (isAdminLike(userProfile) && location.pathname === '/dashboard') {
      navigate('/dashboard/admin', { replace: true });
    }
  }, [userProfile?.rang, location.pathname, navigate]);

  const displayName = useMemo(() =>
    userProfile?.person_full_name ||
    userProfile?.full_name ||
    userProfile?.email?.split('@')[0] ||
    'Utilisateur',
    [userProfile]
  );

  // Chargement du profil
  if (!userProfile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Chargement du profil…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      {Number(userProfile?.rang) === 0 && <EnedisCertExpiryBanner />}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          userRole={userProfile.app_role}
          pdlStats={{ total: 0, connected: 0, pending: 0, errors: 0 }}
          analysesCount={0}
          isSidebarOpen={isSidebarOpen}
          onLinkClick={() => setIsSidebarOpen(false)}
          userName={displayName}
          userProfile={userProfile}
        />

        <main className="flex-1 overflow-y-auto bg-slate-100 p-4 sm:p-6 lg:p-8">
          <div className="mx-auto max-w-7xl">
            <Routes>

              {/* ── Route index ───────────────────────────────────────────── */}
              <Route
                index
                element={
                  isAdminLike(userProfile)
                    ? <div className="text-sm text-gray-500 p-4">
                        Redirection vers l'interface administrateur…
                      </div>
                    : <MainDashboardContent
                        userName={userProfile?.person_full_name}
                        profile={userProfile}
                        pdls={[]}
                        pdlStats={{}}
                      />
                }
              />

              {/* ── Routes utilisateur ────────────────────────────────────── */}
              <Route path="documents"                              element={<MyDocumentsPage />} />
              <Route path="documents/:pdlId"                      element={<PDLDocumentDetailPage />} />
              <Route path="profil"                                element={<ProfilePage />} />

              {/* ── Routes PDL (électricité) ───────────────────────────────── */}
              <Route path="pdl"                                   element={<PDLDashboardPage />} />
              <Route path="pdl-dashboard"                         element={<PDLDashboardPage />} />
              <Route path="pdl/:pdlId/documents/:documentId"      element={<PDLDocumentDetailPage />} />

              {/* ── Routes PCE (gaz) ──────────────────────────────────────── */}
              <Route path="pce"                                   element={<PCEDashboardPage />} />
              <Route path="pce-dashboard"                         element={<PCEDashboardPage />} />
              <Route path="pce/:pceId/documents/:documentId"      element={<PCEDocumentDetailPage />} />

              {/* ── Opportunités ──────────────────────────────────────────── */}
              <Route path="opportunities/:id"                     element={<ClientDetailPage />} />

              {/* ── Routes admin (rang 0 + rang 8) ────────────────────────── */}
              <Route path="admin"                                 element={<AdminDashboard session={session} />} />
              <Route path="admin/client/:id"                      element={<ClientDetailPage />} />
              <Route path="admin/prospect-cspe"                   element={<ProspectCspe />} />
              <Route path="admin/analyse-cspe"                    element={<AnalyseCspe />} />
              <Route path="admin/analyse-cspe/:accountId"         element={<AnalyseCspe />} />
              <Route path="admin/analyse-ticgn"                   element={<AnalyseTicgn />} />
              <Route path="admin/analyse-ticgn/:accountId"        element={<AnalyseTicgn />} />
              <Route path="admin/analyse-turpe"                   element={<AnalyseTurpe />} />
              <Route path="admin/analyse-turpe/:pdlId"            element={<AnalyseTurpe />} />
              <Route path="admin/revenue" element={<RevenueManagementPage />} />
              <Route path="admin/bank" element={<BankPage />} />
              <Route path="admin/lcl" element={<LclPage />} />
              <Route path="admin/cre-updates" element={<CreUpdatesPage />} />
              <Route path="admin/audit-enedis" element={<AuditEnedisGlobal />} />

            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardPage;
